"use client";

import { useState } from "react";
import { addTileAction, rotateKey, signedTapsAction } from "./actions";
import { useFormAction } from "./useFormAction";

const ghost = "inline-flex min-h-10 items-center rounded-[13px] border-2 border-line bg-transparent px-4 text-[13.5px] font-medium";

export function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 2000);
    } catch {
      // clipboard needs https or localhost, the text is selectable as a fallback
    }
  }
  return (
    <button type="button" onClick={copy} className={ghost} aria-live="polite">
      {done ? "Copied" : label}
    </button>
  );
}

export function AddTileForm() {
  const [state, onSubmit, pending] = useFormAction(addTileAction);
  return (
    <form onSubmit={onSubmit} className="mt-3">
      <div className="form-row">
        <label htmlFor="label" className="text-[13px] text-ink-soft">
          New tile name
        </label>
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <input
              // reset after each successful add, the key changes with the message
              key={state?.message ?? "idle"}
              id="label"
              name="label"
              placeholder="Counter 3"
              maxLength={40}
              aria-invalid={!!state?.errors?.label}
              className="slip-input"
              required
            />
            <button type="submit" disabled={pending} className="inline-flex h-10 flex-none items-center rounded-[13px] bg-stamp px-4 text-[13.5px] font-medium text-white disabled:opacity-60">
              {pending ? "Adding" : "Add tile"}
            </button>
          </div>
          {state?.errors?.label && (
            <p role="alert" className="mt-1 text-sm text-alert">
              {state.errors.label}
            </p>
          )}
          {state?.ok && state.message && (
            <p role="status" className="mt-1 text-sm text-paid">
              {state.message}
            </p>
          )}
        </div>
      </div>
    </form>
  );
}

export function KeyPanel() {
  const [state, onSubmit, pending] = useFormAction(() => rotateKey());
  const [confirming, setConfirming] = useState(false);

  if (state?.ok && state.apiKey) {
    return (
      <div className="rounded-[14px] border-2 border-dashed border-line p-3.5">
        <p className="text-sm font-medium">New key. Copy it now, we won&apos;t show it again.</p>
        <p className="num mt-2 text-sm break-all" data-testid="rotated-key">
          {state.apiKey}
        </p>
        <div className="mt-3 flex">
          <CopyButton value={state.apiKey} label="Copy key" />
        </div>
      </div>
    );
  }

  return confirming ? (
    <form onSubmit={onSubmit} className="rounded-[14px] border-2 border-dashed border-line p-3.5">
      <p className="text-sm">The old key stops working at once. Update your till right after.</p>
      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={pending} className="inline-flex min-h-10 items-center rounded-[13px] bg-alert px-4 text-[13.5px] font-medium text-white disabled:opacity-60">
          {pending ? "Creating" : "Replace the key"}
        </button>
        <button type="button" onClick={() => setConfirming(false)} className={ghost}>
          Keep it
        </button>
      </div>
    </form>
  ) : (
    <button type="button" onClick={() => setConfirming(true)} className={ghost}>
      Create a new API key
    </button>
  );
}

// the sticker fills both of these in on every tap, so the zeros only mark where
const PLACEHOLDER_QUERY = "?e=00000000000000000000000000000000&c=0000000000000000";

export function SignedTaps({ tileId, url, sunKey, sunCounter }: { tileId: string; url: string; sunKey: string | null; sunCounter: number }) {
  const [state, onSubmit, pending] = useFormAction(signedTapsAction);
  const [confirming, setConfirming] = useState(false);
  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    setConfirming(false);
    onSubmit(e);
  };

  const feedback = (
    <>
      {state?.errors?.tile && (
        <p role="alert" className="mt-2 text-sm text-alert">
          {state.errors.tile}
        </p>
      )}
      {state?.ok && state.message && (
        <p role="status" className="mt-2 text-sm text-paid">
          {state.message}
        </p>
      )}
    </>
  );

  if (!sunKey) {
    return (
      <div className="col-span-2 border-t-2 border-dotted border-line pt-3">
        <p className="text-[13.5px] text-ink-soft">Signed taps are off. Turn them on for NTAG 424 DNA stickers, so a copied link can&apos;t claim a receipt.</p>
        <form onSubmit={submit} className="mt-2.5 flex">
          <input type="hidden" name="tileId" value={tileId} />
          <input type="hidden" name="turn" value="on" />
          <button type="submit" disabled={pending} className={`${ghost} disabled:opacity-60`}>
            {pending ? "Turning on" : "Turn on signed taps"}
          </button>
        </form>
        {feedback}
      </div>
    );
  }

  const writeUrl = `${url}${PLACEHOLDER_QUERY}`;
  return (
    <div className="col-span-2 border-t-2 border-dotted border-line pt-3">
      <div className="flex items-center gap-3">
        <span className="stamp-tag flex-none px-2! py-px! text-[11px]!">SIGNED</span>
        <p className="text-[13.5px] text-ink-soft">{sunCounter < 0 ? "No taps yet" : `Taps so far: ${sunCounter}`}</p>
      </div>

      <details className="mt-2">
        <summary className="flex min-h-10 cursor-pointer items-center text-[13.5px] font-medium">Sticker setup</summary>
        <div className="pb-1">
          <p className="text-[13px] text-ink-soft">Link to write</p>
          <p className="num mt-1 mb-2 text-xs break-all select-all" data-testid="sun-url">
            {writeUrl}
          </p>
          <CopyButton value={writeUrl} label="Copy URL" />
          <p className="mt-3 text-[13px] text-ink-soft">Key</p>
          <p className="num mt-1 mb-2 text-xs break-all select-all" data-testid="sun-key">
            {sunKey}
          </p>
          <CopyButton value={sunKey} label="Copy key" />
          <p className="mt-3 text-[13px] text-ink-soft">
            In NXP TagWriter, turn on SDM. Mirror the UID and counter, encrypted, at e=. Put the MAC at c=, with the MAC input starting at the same spot. Use this key for both SDM read keys. The zeros are placeholders the
            sticker fills in on every tap.
          </p>
        </div>
      </details>

      {confirming ? (
        <form onSubmit={submit} className="mt-2.5 rounded-[14px] border-2 border-dashed border-line p-3.5">
          <input type="hidden" name="tileId" value={tileId} />
          <input type="hidden" name="turn" value="on" />
          <p className="text-sm">The sticker stops claiming until you program the new key.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="submit" disabled={pending} className="inline-flex min-h-10 items-center rounded-[13px] bg-alert px-4 text-[13.5px] font-medium text-white disabled:opacity-60">
              {pending ? "Making" : "Make a new key"}
            </button>
            <button type="button" onClick={() => setConfirming(false)} className={ghost}>
              Keep it
            </button>
          </div>
        </form>
      ) : (
        <div className="mt-2.5 flex flex-wrap gap-2">
          <button type="button" onClick={() => setConfirming(true)} disabled={pending} className={`${ghost} disabled:opacity-60`}>
            Make a new key
          </button>
          <form onSubmit={submit} className="flex">
            <input type="hidden" name="tileId" value={tileId} />
            <input type="hidden" name="turn" value="off" />
            <button type="submit" disabled={pending} className={`${ghost} disabled:opacity-60`}>
              {pending ? "Turning off" : "Turn off"}
            </button>
          </form>
        </div>
      )}
      {feedback}
    </div>
  );
}
